'use client'

import dynamic from 'next/dynamic'
import Image from 'next/image'
import Link from 'next/link'
import { memo, useCallback, useEffect, useRef, useState } from 'react'
import type { SwiperRef } from 'swiper/react'

import { HomeHeroSlide } from '@/screens/Home'

import { getBannerClick, getSaleView, pushToDataLayer } from '@/shared/integrations/yandex-metric'
import { BASE_URL } from '@/shared/lib/const/env'

import { Pagination } from './Pagination/Pagination'

import cl from './Slider.module.scss'
import swiperCl from './SwiperSlider/SwiperSlider.module.scss'

const SwiperSlider = dynamic(() => import('./SwiperSlider/SwiperSlider').then(m => m.SwiperSlider), {
	ssr: false
})

interface SliderProps {
	items: HomeHeroSlide[]
	isMobile: boolean
}

export const Slider = memo((props: SliderProps) => {
	const { items = [], isMobile } = props
	const [shouldMountSwiper, setShouldMountSwiper] = useState(false)
	const [isSwiperReady, setIsSwiperReady] = useState(false)

	const [activeIndex, setActiveIndex] = useState(0)

	const swiperRef = useRef<SwiperRef>(null)

	// Defer Swiper init off the load-critical window: the static LCP image is already
	// visible, so we mount the (heavy) carousel only once the main thread is idle.
	useEffect(() => {
		if (typeof window.requestIdleCallback === 'function') {
			const id = window.requestIdleCallback(() => setShouldMountSwiper(true), { timeout: 2000 })

			return () => window.cancelIdleCallback(id)
		}

		const id = window.setTimeout(() => setShouldMountSwiper(true), 200)

		return () => window.clearTimeout(id)
	}, [])

	const onSwiperReady = useCallback(() => {
		setIsSwiperReady(true)
	}, [])

	const onPaginationClick = (index: number) => {
		swiperRef.current?.swiper.slideTo(index)
	}

	const onSlideChange = (index: number, item: HomeHeroSlide) => {
		setActiveIndex(prevIndex => (prevIndex === index ? prevIndex : index))
		pushToDataLayer(getSaleView(item, `Главный слайдер`))
	}

	const onSlideClick = (item: HomeHeroSlide) => {
		pushToDataLayer(getBannerClick(item, `Главный слайдер`))
	}

	const firstItem = items[0]
	const firstImageSrc = firstItem ? (isMobile ? firstItem.IMG_MOBILE : firstItem.IMG) : null

	return (
		<>
			<div className={cl.root}>
				{firstItem && firstImageSrc ? (
					<Link href={firstItem.LINK_VALUE || `/`} prefetch={false} onClick={() => onSlideClick(firstItem)}>
						<div
							className={swiperCl.image}
							style={{
								opacity: isSwiperReady ? 0 : 1,
								pointerEvents: isSwiperReady ? 'none' : 'auto',
								transition: 'opacity 150ms ease-out'
							}}>
							<Image
								src={`${BASE_URL}${firstImageSrc}`}
								alt={firstItem.NAME}
								fill
								priority
								fetchPriority="high"
								sizes="100vw"
								quality={75}
							/>
						</div>
					</Link>
				) : null}

				{shouldMountSwiper ? (
					<>
						<div
							style={{
								position: 'absolute',
								inset: 0,
								opacity: isSwiperReady ? 1 : 0,
								pointerEvents: isSwiperReady ? 'auto' : 'none',
								transition: 'opacity 150ms ease-out'
							}}>
							<SwiperSlider
								items={items}
								isMobile={isMobile}
								onSlideChange={onSlideChange}
								onSlideClick={onSlideClick}
								onReady={onSwiperReady}
								ref={swiperRef}
							/>
						</div>
						{items.length > 1 ? (
							<Pagination count={items.length} activeIndex={activeIndex} onClick={onPaginationClick} />
						) : null}
					</>
				) : null}
			</div>
		</>
	)
})
